import { Controller } from "@hotwired/stimulus"

// New offer pop-up + offers table (Admin › Company › Payment and
// Admin › Subscriptions › Offers), Phelipe 2026-10-30.
// Picking a plan fills the price (until it's edited), the price note and the
// bullets; "Let them pick" shows which option is picked first; "Who can use
// the link" is anyone at the company after sign-in, or anyone (public link).
// The right column previews the customer's offer page as you type.
// Create link posts JSON, inserts the new row and shows the link to copy;
// the pencil opens the same pop-up to edit an offer and swaps its row in;
// the clock loads the offer's history — no reloads.
// 2026-10-31: "Also allow direct wire" (only when yearly is possible and
// ConsLog's wire details are saved), Wire details, Send by email (envelope)
// and Mark paid (a wire arrived) — each a small pop-up that swaps the row.
// Trash: the bin on a row deletes an unpaid offer (after a confirm); the
// trash can by Wire details lists deleted ones with Restore.
export default class extends Controller {
  static targets = [
    "modal", "form", "done", "company", "plan", "price", "note", "cycleBox", "defaultBox",
    "defaultButton", "audienceBox", "expires", "headline", "bullets", "error", "link", "copyButton",
    "createButton", "cancelButton", "rows", "empty", "doneNote", "title", "subtitle", "historyModal", "historyBody",
    "pvCompany", "pvExpires", "pvPlan", "pvHeadline", "pvBullets", "pvOptions", "pvButton", "pvNote", "pvAudience",
    "allowWire", "wireBox", "wireHint", "wireSetup", "pvWire", "pvWireTip", "allowPlanChange", "showAllPlans", "pvAllPlans",
    "emailModal", "emailSubtitle", "emailFrom", "emailTo", "emailCc", "emailNote", "emailError", "emailButton", "emailDraftButton",
    "paidModal", "paidSubtitle", "paidAmount", "paidOn", "paidReference", "paidReceiptTo", "paidError", "paidButton",
    "wireModal", "wireError", "wireButton",
    "trashCount", "trashModal", "trashBody", "deleteModal", "deleteLabel", "deleteError", "deleteButton"
  ]
  static values = {
    createUrl: String, companyId: String, companyName: String, context: String, plans: Object,
    wireReady: Boolean, wireUrl: String, trashUrl: String
  }

  connect() {
    this.defaultCycle = "monthly"
    this.priceTouched = false
    this.editing = null
    this.initialPlan = this.planTarget.value
    this.initialExpires = this.expiresTarget.value
  }

  open() {
    this.editing = null
    this._showForm("New offer", this.companyNameValue ? `For ${this.companyNameValue}` : "", "Create link")
    if (this.hasCompanyTarget) this.companyTarget.disabled = false
    this.planTarget.value = this.initialPlan
    this._check("offer_cycle", "pick")
    this.defaultCycle = "monthly"
    this._check("offer_audience", "company")
    this._setExpires(this.initialExpires)
    this.allowWireTarget.checked = false
    this.allowPlanChangeTarget.checked = false
    this.showAllPlansTarget.checked = false
    this.priceTouched = false
    this.headlineTouched = false
    this.planChanged()
    this.cycleChanged()
    this.audienceChanged()
    this.modalTarget.classList.remove("hidden")
  }

  // The pencil on a row: the same pop-up, filled with that offer.
  edit(event) {
    const offer = JSON.parse(event.currentTarget.dataset.offer || "{}")
    this.editing = offer
    this._showForm("Edit offer", `For ${offer.company_name}`, "Save changes")
    if (this.hasCompanyTarget) {
      this.companyTarget.value = String(offer.company_id)
      this.companyTarget.disabled = true
    }
    this.planTarget.value = offer.plan
    this.priceTarget.value = this._money(offer.monthly_amount)
    this.priceTouched = true
    this.headlineTarget.value = offer.headline || ""
    this.headlineTouched = true
    const plan = this.plansValue[offer.plan]
    if (plan) this.noteTarget.textContent = plan.note
    this._check("offer_cycle", offer.cycle)
    this.defaultCycle = offer.default_cycle || "monthly"
    this._check("offer_audience", offer.audience || "company")
    this._setExpires(offer.expires_on)
    this.allowWireTarget.checked = !!offer.allow_wire
    this.allowPlanChangeTarget.checked = !!offer.allow_plan_change
    this.showAllPlansTarget.checked = !!offer.show_all_plans
    this._renderBullets(offer.bullets)
    this.cycleChanged()
    this.audienceChanged()
    this.modalTarget.classList.remove("hidden")
  }

  async history(event) {
    this.historyBodyTarget.textContent = "Loading…"
    this.historyModalTarget.classList.remove("hidden")
    try {
      const response = await fetch(event.currentTarget.dataset.url, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error()
      this.historyBodyTarget.innerHTML = await response.text()
    } catch {
      this.historyBodyTarget.textContent = "Couldn't load the history. Try again."
    }
  }

  closeHistory() {
    this.historyModalTarget.classList.add("hidden")
  }

  close() {
    this.modalTarget.classList.add("hidden")
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  planChanged() {
    const plan = this.plansValue[this.planTarget.value]
    if (!plan) return
    if (!this.priceTouched) this.priceTarget.value = this._money(plan.price)
    if (!this.headlineTouched) this.headlineTarget.value = plan.headline || ""
    this.noteTarget.textContent = plan.note
    this._renderBullets(plan.bullets)
  }

  priceEdited() {
    this.priceTouched = true
  }

  headlineEdited() {
    this.headlineTouched = true
  }

  resetBullets() {
    const plan = this.plansValue[this.planTarget.value]
    if (plan) this._renderBullets(plan.bullets)
  }

  addBullet() {
    this.bulletsTarget.appendChild(this._bulletRow(""))
    this.bulletsTarget.lastElementChild.querySelector("input").focus()
    this.refreshPreview()
  }

  removeBullet(event) {
    event.currentTarget.closest("[data-bullet-row]").remove()
    this.refreshPreview()
  }

  cycleChanged() {
    const cycle = this._cycle()
    this._paintBoxes(this.cycleBoxTargets, "cycle", cycle)
    this.defaultBoxTarget.classList.toggle("hidden", cycle !== "pick")
    this._paintDefault()
    this._paintWire()
  }

  pickDefault(event) {
    this.defaultCycle = event.currentTarget.dataset.default
    this._paintDefault()
    this.refreshPreview()
  }

  audienceChanged() {
    this._paintBoxes(this.audienceBoxTargets, "audience", this._audience())
    this.refreshPreview()
  }

  // The right column: the customer's offer page, redrawn from the form.
  refreshPreview() {
    if (!this.hasPvPlanTarget) return
    const plan = this.plansValue[this.planTarget.value] || {}
    const company = this._companyName()
    const monthly = this._cents(this.priceTarget.value)
    const yearlyMonthly = Math.floor((monthly * 0.8) / 100) * 100
    const yearlyTotal = yearlyMonthly * 12
    const intervals = this._intervals()
    const picked = intervals.length > 1 ? (this.defaultCycle === "yearly" ? "year" : "month") : intervals[0]
    const isPublic = this._audience() === "public"

    this.pvCompanyTarget.textContent = company
    this.pvExpiresTarget.textContent = this._expiresText()
    this.pvPlanTarget.textContent = plan.name || ""
    this.pvHeadlineTarget.textContent = this.headlineTarget.value.trim()
    this.pvHeadlineTarget.classList.toggle("hidden", !this.headlineTarget.value.trim())

    const bullets = Array.from(this.bulletsTarget.querySelectorAll("input")).map((i) => i.value.trim()).filter(Boolean)
    this.pvBulletsTarget.replaceChildren(...bullets.map((text) => {
      const li = document.createElement("li")
      li.className = "flex gap-2 text-[13px] text-[#1c1917]"
      const dot = document.createElement("span")
      dot.className = "mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-[#c2410c]"
      dot.setAttribute("aria-hidden", "true")
      const label = document.createElement("span")
      label.textContent = text
      li.append(dot, label)
      return li
    }))

    const options = []
    if (intervals.includes("month")) {
      options.push(this._previewOption("Monthly", "Cancel any time", `${this._dollars(monthly)}/mo`, picked === "month"))
    }
    if (intervals.includes("year")) {
      options.push(this._previewOption("Yearly · save 20%", `${this._dollars(yearlyTotal)} billed once a year`, `${this._dollars(yearlyMonthly)}/mo`, picked === "year"))
    }
    this.pvOptionsTarget.replaceChildren(...options)

    this.pvButtonTarget.textContent = picked === "year"
      ? `Pay by card or bank · ${this._dollars(yearlyTotal)}/yr`
      : `Pay by card or bank · ${this._dollars(monthly)}/mo`
    this.pvNoteTarget.textContent = isPublic
      ? `Card, Apple Pay or bank (ACH), on a secure Stripe page. Anyone with this link can pay. The plan goes to ${company}.`
      : "Card, Apple Pay or bank (ACH), on a secure Stripe page."
    this.pvAudienceTarget.textContent = isPublic ? "No sign-in · anyone with the link" : `Signs in first · anyone at ${company}`
    // Card/bank or wire (2026-10-31): wire sits under an "or" on Yearly; on
    // Monthly a line points them to Yearly if they want to wire.
    this.pvWireTarget.classList.toggle("hidden", !(this.allowWireTarget.checked && picked === "year"))
    this.pvWireTipTarget.classList.toggle("hidden", !(this.allowWireTarget.checked && picked === "month"))
    this.pvAllPlansTarget.classList.toggle("hidden", !this.showAllPlansTarget.checked)
  }

  async save() {
    const editing = this.editing
    const companyId = editing ? editing.company_id : (this.hasCompanyTarget ? this.companyTarget.value : this.companyIdValue)
    const audience = this._audience()
    const body = {
      company_id: companyId,
      plan: this.planTarget.value,
      monthly_amount: this.priceTarget.value,
      cycle: this._cycle(),
      default_cycle: this.defaultCycle,
      audience: audience,
      expires_on: this.expiresTarget.value,
      headline: this.headlineTarget.value,
      bullets: Array.from(this.bulletsTarget.querySelectorAll("input")).map((i) => i.value),
      allow_wire: this.allowWireTarget.checked,
      allow_plan_change: this.allowPlanChangeTarget.checked,
      show_all_plans: this.showAllPlansTarget.checked,
      context: this.contextValue
    }
    this.createButtonTarget.disabled = true
    this.errorTarget.classList.add("hidden")
    try {
      const response = await fetch(editing ? editing.url : this.createUrlValue, {
        method: editing ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
        body: JSON.stringify(body)
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "Couldn't save the offer.")

      if (editing) {
        const row = this.rowsTarget.querySelector(`[data-offer-id="${editing.id}"]`)
        if (row) row.outerHTML = data.row_html
        this.close()
        this._showToast("Offer saved.")
        return
      }

      const company = this._companyName()
      this.rowsTarget.insertAdjacentHTML("afterbegin", data.row_html)
      if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
      this.linkTarget.textContent = data.url
      this.copyButtonTarget.dataset.url = data.url
      this.doneNoteTarget.textContent = audience === "public"
        ? `Link ready. Anyone with it can pay, no sign-in; the plan goes to ${company}. Share it carefully.`
        : `Link ready. Send it to anyone at ${company}; they sign in to ConsLog, then pay.`
      this.formTarget.classList.add("hidden")
      this.doneTarget.classList.remove("hidden")
      this.createButtonTarget.classList.add("hidden")
      this.cancelButtonTarget.textContent = "Close"
      this._showToast("Offer link created.")
    } catch (error) {
      this.errorTarget.textContent = error.message
      this.errorTarget.classList.remove("hidden")
      this.createButtonTarget.disabled = false
    }
  }

  // ── Send by email (envelope on a row) ──

  openEmail(event) {
    const offer = JSON.parse(event.currentTarget.dataset.offer || "{}")
    this.emailing = offer
    this.emailSubtitleTarget.textContent = `${offer.company_name} · ${this.plansValue[offer.plan]?.name || offer.plan}`
    // To starts empty so the right person is typed in (Phelipe 2026-10-31),
    // unless a saved draft fills it all back in.
    const draft = offer.email_draft || {}
    this.emailFromTarget.value = draft.from || this.emailFromTarget.dataset.default
    this.emailToTarget.value = draft.to || ""
    this.emailCcTarget.value = draft.cc || ""
    this.emailNoteTarget.value = draft.note || ""
    this.emailErrorTarget.classList.add("hidden")
    this.emailButtonTarget.disabled = false
    this.emailModalTarget.classList.remove("hidden")
    this.emailToTarget.focus()
  }

  closeEmail() {
    this.emailModalTarget.classList.add("hidden")
  }

  // Keeps From, To, CC and the note on the offer for later (2026-10-31).
  async saveEmailDraft() {
    const offer = this.emailing
    if (!offer) return
    await this._post(offer.email_draft_url, "PATCH", {
      from: this.emailFromTarget.value, to: this.emailToTarget.value, cc: this.emailCcTarget.value,
      note: this.emailNoteTarget.value
    }, this.emailDraftButtonTarget, this.emailErrorTarget, (data) => {
      this._swapRow(offer.id, data.row_html)
      this.closeEmail()
      this._showToast("Draft saved. It opens again from the envelope.")
    })
  }

  async sendEmail() {
    const offer = this.emailing
    if (!offer) return
    await this._post(offer.send_email_url, "POST", {
      from: this.emailFromTarget.value, to: this.emailToTarget.value, cc: this.emailCcTarget.value,
      note: this.emailNoteTarget.value
    }, this.emailButtonTarget, this.emailErrorTarget, (data) => {
      this._swapRow(offer.id, data.row_html)
      this.closeEmail()
      this._showToast("Offer emailed.")
    })
  }

  // ── Mark paid (a direct wire arrived) ──

  openMarkPaid(event) {
    const offer = JSON.parse(event.currentTarget.dataset.offer || "{}")
    this.paying = offer
    this.paidSubtitleTarget.textContent = `${offer.company_name} · ${this.plansValue[offer.plan]?.name || offer.plan} · yearly`
    this.paidAmountTarget.value = this._money(offer.yearly_total)
    const picker = this.paidOnTarget._flatpickr
    if (picker) picker.setDate(new Date(), false)
    this.paidReferenceTarget.value = offer.wire_reference || ""
    this.paidReceiptToTarget.value = offer.receipt_to || ""
    this.paidErrorTarget.classList.add("hidden")
    this.paidButtonTarget.disabled = false
    this.paidModalTarget.classList.remove("hidden")
  }

  closePaid() {
    this.paidModalTarget.classList.add("hidden")
  }

  async markPaid() {
    const offer = this.paying
    if (!offer) return
    await this._post(offer.mark_paid_url, "POST", {
      amount: this.paidAmountTarget.value, received_on: this.paidOnTarget.value,
      reference: this.paidReferenceTarget.value, receipt_to: this.paidReceiptToTarget.value
    }, this.paidButtonTarget, this.paidErrorTarget, (data) => {
      this._swapRow(offer.id, data.row_html)
      this.closePaid()
      this._showToast(data.message || "Marked paid.")
    })
  }

  // ── ConsLog's wire details ──

  openWire() {
    this.wireErrorTarget.classList.add("hidden")
    this.wireButtonTarget.disabled = false
    this.wireModalTarget.classList.remove("hidden")
  }

  closeWire() {
    this.wireModalTarget.classList.add("hidden")
  }

  async saveWire() {
    const body = {}
    this.wireModalTarget.querySelectorAll("[data-wire-field]").forEach((input) => { body[input.dataset.wireField] = input.value })
    await this._post(this.wireUrlValue, "PATCH", body, this.wireButtonTarget, this.wireErrorTarget, (data) => {
      this.wireReadyValue = !!data.ready
      this._paintWire()
      // Saved from inside the New offer pop-up: tick wire for them.
      if (data.ready && !this.modalTarget.classList.contains("hidden") && !this.allowWireTarget.disabled) {
        this.allowWireTarget.checked = true
        this.refreshPreview()
      }
      this.closeWire()
      this._showToast(data.ready ? "Wire details saved." : "Saved. Add the account name, bank, account number and a routing or SWIFT number to allow wires.")
    })
  }

  // ── Trash ──

  askDelete(event) {
    const button = event.currentTarget
    this.deleting = { url: button.dataset.url, row: button.closest("[data-offer-id]") }
    this.deleteLabelTarget.textContent = button.dataset.label || ""
    this.deleteErrorTarget.classList.add("hidden")
    this.deleteButtonTarget.disabled = false
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDelete() {
    this.deleteModalTarget.classList.add("hidden")
  }

  async confirmDelete() {
    const deleting = this.deleting
    if (!deleting) return
    await this._post(deleting.url, "DELETE", { company_id: this.companyIdValue }, this.deleteButtonTarget, this.deleteErrorTarget, (data) => {
      deleting.row?.remove()
      if (this.hasEmptyTarget && !this.rowsTarget.querySelector("[data-offer-id]")) this.emptyTarget.classList.remove("hidden")
      this._setTrashCount(data.trash_count)
      this.closeDelete()
      this._showToast("Offer moved to the trash. Its link no longer works.")
    })
  }

  async openTrash() {
    this.trashBodyTarget.textContent = "Loading…"
    this.trashModalTarget.classList.remove("hidden")
    try {
      const response = await fetch(this.trashUrlValue, { headers: { Accept: "text/html" } })
      if (!response.ok) throw new Error()
      this.trashBodyTarget.innerHTML = await response.text()
    } catch {
      this.trashBodyTarget.textContent = "Couldn't load the trash. Try again."
    }
  }

  closeTrash() {
    this.trashModalTarget.classList.add("hidden")
  }

  async restore(event) {
    const button = event.currentTarget
    const item = button.closest("[data-trash-offer-id]")
    const error = document.createElement("p")
    await this._post(button.dataset.url, "POST", { context: this.contextValue, company_id: this.companyIdValue }, button, error, (data) => {
      this.rowsTarget.insertAdjacentHTML("afterbegin", data.row_html)
      if (this.hasEmptyTarget) this.emptyTarget.classList.add("hidden")
      item?.remove()
      if (!this.trashBodyTarget.querySelector("[data-trash-offer-id]")) {
        this.trashBodyTarget.innerHTML = '<p class="py-6 text-center text-[13px] text-text-muted">The trash is empty.</p>'
      }
      this._setTrashCount(data.trash_count)
      this._showToast("Offer restored. Its link works again.")
    })
    if (!error.classList.contains("hidden") && error.textContent) this._showToast(error.textContent, "error")
  }

  _setTrashCount(count) {
    if (!this.hasTrashCountTarget || count === undefined) return
    this.trashCountTarget.textContent = count
    this.trashCountTarget.classList.toggle("hidden", Number(count) === 0)
  }

  async copy(event) {
    const button = event.currentTarget
    const url = button.dataset.url
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      this._showToast("Link copied.")
    } catch {
      window.prompt("Copy this link:", url)
    }
  }

  // ── helpers ──

  // Wire is for yearly only, and needs ConsLog's wire details first.
  _paintWire() {
    if (!this.hasAllowWireTarget) return
    const yearlyPossible = this._cycle() !== "monthly"
    const usable = yearlyPossible && this.wireReadyValue
    this.allowWireTarget.disabled = !usable
    if (!usable) this.allowWireTarget.checked = false
    this.wireBoxTarget.classList.toggle("opacity-60", !usable)
    this.wireSetupTarget.classList.toggle("hidden", this.wireReadyValue)
    this.wireHintTarget.textContent = !this.wireReadyValue
      ? "Add ConsLog's bank details first, then tick this."
      : !yearlyPossible
        ? "Yearly only: pick Yearly or Let them pick to allow it."
        : "Yearly only. They pick on the offer page: Stripe or wire. You click Mark paid when the wire arrives."
    this.refreshPreview()
  }

  async _post(url, method, body, button, errorEl, onSuccess) {
    button.disabled = true
    errorEl.classList.add("hidden")
    try {
      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this._csrf() },
        // context: on Subscriptions › Offers the returned row carries the
        // Company cell; without it a swapped row lost a column (2026-10-31).
        body: JSON.stringify({ context: this.contextValue, ...body })
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || "Something went wrong. Try again.")
      onSuccess(data)
    } catch (error) {
      errorEl.textContent = error.message
      errorEl.classList.remove("hidden")
    } finally {
      button.disabled = false
    }
  }

  _swapRow(id, html) {
    const row = this.rowsTarget.querySelector(`[data-offer-id="${id}"]`)
    if (row && html) row.outerHTML = html
  }

  _showForm(title, subtitle, buttonLabel) {
    this.formTarget.classList.remove("hidden")
    this.doneTarget.classList.add("hidden")
    this.createButtonTarget.classList.remove("hidden")
    this.createButtonTarget.disabled = false
    this.createButtonTarget.textContent = buttonLabel
    this.cancelButtonTarget.textContent = "Cancel"
    this.errorTarget.classList.add("hidden")
    this.titleTarget.textContent = title
    this.subtitleTarget.textContent = subtitle
  }

  _check(name, value) {
    const input = this.element.querySelector(`input[name="${name}"][value="${value}"]`)
    if (input) input.checked = true
  }

  _setExpires(value) {
    const picker = this.expiresTarget._flatpickr
    if (picker) {
      if (value) picker.setDate(value, false)
      else picker.clear(false)
    } else {
      this.expiresTarget.value = value || ""
    }
  }

  _cycle() {
    const checked = this.element.querySelector('input[name="offer_cycle"]:checked')
    return checked ? checked.value : "pick"
  }

  _audience() {
    const checked = this.element.querySelector('input[name="offer_audience"]:checked')
    return checked ? checked.value : "company"
  }

  _intervals() {
    const cycle = this._cycle()
    if (cycle === "monthly") return ["month"]
    if (cycle === "yearly") return ["year"]
    return ["month", "year"]
  }

  _companyName() {
    if (this.editing) return this.editing.company_name
    if (this.hasCompanyTarget) {
      const option = this.companyTarget.selectedOptions[0]
      return option && option.value ? option.textContent.trim() : "the company"
    }
    return this.companyNameValue || "the company"
  }

  _expiresText() {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(this.expiresTarget.value || "")
    if (!match) return ""
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    return `Offer expires ${date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`
  }

  _previewOption(title, sub, price, selected) {
    const box = document.createElement("div")
    box.className = `flex items-center justify-between gap-3 rounded-lg border-2 px-3 py-2 ${selected ? "border-[#c2410c] bg-[#fff7ed]" : "border-[#e7e5e4] bg-[#ffffff]"}`
    const left = document.createElement("span")
    const name = document.createElement("span")
    name.className = "block text-[13px] font-bold text-[#1c1917]"
    name.textContent = title
    const note = document.createElement("span")
    note.className = "block text-[13px] text-[#57534e]"
    note.textContent = sub
    left.append(name, note)
    const amount = document.createElement("span")
    amount.className = "text-base font-extrabold text-[#1c1917]"
    amount.textContent = price
    box.append(left, amount)
    return box
  }

  _paintBoxes(boxes, key, value) {
    boxes.forEach((box) => {
      const on = box.dataset[key] === value
      box.classList.toggle("border-brand", on)
      box.classList.toggle("bg-brand-50", on)
      box.classList.toggle("border-gray-300", !on)
    })
  }

  _paintDefault() {
    this.defaultButtonTargets.forEach((button) => {
      const on = button.dataset.default === this.defaultCycle
      button.classList.toggle("bg-brand", on)
      button.classList.toggle("text-white", on)
      button.classList.toggle("border-brand", on)
      button.classList.toggle("bg-white", !on)
      button.classList.toggle("text-text-heading", !on)
      button.classList.toggle("border-gray-300", !on)
      button.setAttribute("aria-pressed", on ? "true" : "false")
    })
  }

  _renderBullets(bullets) {
    this.bulletsTarget.replaceChildren(...(bullets || []).map((text) => this._bulletRow(text)))
    this.refreshPreview()
  }

  _bulletRow(text) {
    const row = document.createElement("div")
    row.className = "flex items-center gap-2"
    row.dataset.bulletRow = ""
    const dot = document.createElement("span")
    dot.className = "h-2 w-2 flex-none rounded-full bg-orange-700"
    dot.setAttribute("aria-hidden", "true")
    const input = document.createElement("input")
    input.type = "text"
    input.value = text
    input.maxLength = 140
    input.setAttribute("aria-label", "Bullet text")
    input.className = "form-input min-w-0 flex-1"
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "flex h-9 w-9 flex-none items-center justify-center rounded-lg border border-border-light text-text-muted hover:bg-gray-100"
    remove.setAttribute("aria-label", "Remove bullet")
    remove.dataset.action = "admin--builder-offer#removeBullet"
    remove.textContent = "×"
    row.append(dot, input, remove)
    return row
  }

  _cents(value) {
    const number = parseFloat(String(value || "").replace(/[,$\s]/g, ""))
    return Number.isFinite(number) ? Math.round(number * 100) : 0
  }

  // Same as BuilderPlan.dollars: "$1,499", or "$1,499.50" when there are cents.
  _dollars(cents) {
    const whole = cents % 100 === 0
    return "$" + (cents / 100).toLocaleString("en-US", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })
  }

  _money(value) {
    const number = Number(value)
    return Number.isFinite(number) ? number.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ""
  }

  _csrf() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const msg = document.createElement("p")
    msg.className = "flex-1 text-sm"
    msg.textContent = message
    el.appendChild(msg)
    document.body.prepend(el)
  }
}
